import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWalkthrough } from '../context/walkthroughContext';
import { useAuth } from '../context/authContext';
import { useWalkthroughChef } from '../context/WalkthroughChefContext';
import WalkthroughModal from './WalkthroughModal';
import WalkthroughOverlay from './WalkthroughOverlay';
import ScrollIndicator from './ScrollIndicator';

const WalkthroughManager = ({ className = "" }) => {
  const { showWalkthrough, completeWalkthrough, closeWalkthrough, stopWalkthrough } = useWalkthrough();
  const { user, refreshUserProfile } = useAuth();
  const { setWalkthroughChef, clearWalkthroughChef } = useWalkthroughChef();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChefTabVisible, setIsChefTabVisible] = useState(false);
  const [isChefTabExpanded, setIsChefTabExpanded] = useState(false);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [overlaySubMessage, setOverlaySubMessage] = useState('');
  const [chefState, setChefState] = useState('talking');
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const navigationProgressedRef = useRef(false);
  const completionTrackedRef = useRef(false);

  // Walkthrough step definitions
  // To create new walkthroughs, simply add steps with:
  // - type: 'modal' for center modals, 'highlight' for element highlighting
  // - targetSelector: CSS selector for the element to highlight (use data-walkthrough attributes)
  // - message/subMessage: Text to show in the chef tab
  // - chefState: 'talking', 'fire', etc.
  // - position: 'left' or 'right' for chef tab placement
  const walkthroughSteps = [
    // Step 0: Initial welcome modal on Explore page
    {
      type: 'modal',
      title: 'Welcome to 4RKED!',
      message: "Hi there! I'm Chef, your cooking companion. I'm here to help you get started with creating amazing recipes!",
      subMessage: "Let me show you around and help you create your first recipe step by step.",
      chefState: 'talking',
      showStartButton: true,
      startButtonText: "Let's Start!",
      showDoNotShowAgain: true
    },
    // Step 1: Highlight Recipes button in navigation
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="recipes-nav-button"]',
      message: "First, let's go to your Recipes page where you'll manage all your recipes!",
      subMessage: "Click the 'RECIPES' button in the navigation to get started.",
      chefState: 'talking',
      position: 'bottom'
      // No navigateTo - let user click the highlighted element
    },
    // Step 2: Highlight plus tab on My Recipes page
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="create-button"]',
      navigateTo: '/recipes', // Ensure we're on recipes page
      message: "Perfect! Now look for the plus tab on the left or bottom edge of the screen.",
      subMessage: "Click the plus tab to start creating your first recipe!",
      chefState: 'talking',
      position: 'right',
      waitForPage: '/recipes' // Wait for recipes page to load before highlighting
    },
    // Step 3: Wait for user to navigate to create page, then show Smart Transfer option
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="smart-transfer"]',
      navigateTo: '/create',
      message: "Great! You can create recipes in several ways. First, let's look at Smart Transfer - you can import recipes from anywhere on the web.",
      subMessage: "You can paste a recipe URL here, or switch to paste text mode. Let me show you both options!",
      chefState: 'talking',
      position: 'left',
      waitForPage: '/create',
      showBackButton: true
    },
    // Step 4: Highlight PASTE TEXT button option
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="paste-text-button"]',
      message: "You can also paste recipe text directly! Click the 'PASTE TEXT' button to switch modes.",
      subMessage: "This is great for copying recipes from emails, documents, or anywhere you have text.",
      chefState: 'talking',
      position: 'right',
      showBackButton: true
    },
    // Step 5: Highlight recipe type toggle (required before transfer)
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="multi-component-toggle"]',
      message: "Before transferring, you need to select the recipe type. Is this a simple recipe or multi-component?",
      subMessage: "Simple recipes have one list of ingredients and steps. Multi-component recipes have separate parts (like pie crust + filling). Click the question mark to learn more, then select your recipe type.",
      chefState: 'talking',
      position: 'left',
      showBackButton: true
    },
    // Step 6: Show the text area after switching to paste mode
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="smart-transfer"]',
      message: "Perfect! Now you can paste your recipe text here. The AI will extract all the details for you.",
      subMessage: "You can switch back to URL mode anytime, or continue to see how to manually add recipes.",
      chefState: 'talking',
      position: 'left',
      showBackButton: true,
      waitForCondition: true,
      conditionCheck: 'pasteTextMode'
    },
    // Step 7: Show manual recipe entry - highlight recipe name field
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="recipe-name-field"]',
      message: "You can also create recipes manually! Let's start by entering a recipe name.",
      subMessage: "Type your recipe name in this field. You can create recipes from scratch without using Smart Transfer.",
      chefState: 'talking',
      position: 'right',
      showBackButton: true
    },
    // Step 8: Show ingredients section for simple recipe
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="ingredients-section"]',
      message: "Now let's add ingredients. Click 'ADD INGREDIENT' to add each ingredient one by one.",
      subMessage: "For a simple recipe, all ingredients go in one list. We'll show you multi-component recipes next!",
      chefState: 'talking',
      position: 'left',
      showBackButton: true
    },
    // Step 8: Show prerequisites section
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="prerequisites-section"]',
      message: "Prerequisites are preparation steps done before cooking starts, like preheating the oven.",
      subMessage: "Add any prerequisites your recipe needs before the main cooking begins.",
      chefState: 'talking',
      position: 'right',
      showBackButton: true
    },
    // Step 9: Show instructions/steps section
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="steps-section"]',
      message: "Finally, add your cooking instructions step by step.",
      subMessage: "These are the main cooking steps that will guide you through making the recipe.",
      chefState: 'talking',
      position: 'left',
      showBackButton: true
    },
    // Step 10: Show multi-component toggle
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="multi-component-toggle"]',
      message: "Some recipes have multiple components! Like a pie with crust and filling, or a burger with bun, patty, and sauce.",
      subMessage: "Click this button to switch to multi-component mode. Each component has its own ingredients and steps.",
      chefState: 'talking',
      position: 'right',
      showBackButton: true
    },
    // Step 11: Show add component button (after switching to multi-component)
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="add-component-button"]',
      message: "Perfect! Now you can add components. Click 'ADD COMPONENT' to create each part of your recipe.",
      subMessage: "For example, a pie might have 'Crust' and 'Filling' as separate components.",
      chefState: 'talking',
      position: 'left',
      showBackButton: true,
      waitForCondition: true,
      conditionCheck: 'multiComponentMode'
    },
    // Step 12: Show assembly section
    {
      type: 'highlight',
      targetSelector: '[data-walkthrough="assembly-section"]',
      message: "After creating all components, you'll need assembly instructions - how to put everything together.",
      subMessage: "This section shows how to combine the components into the final dish.",
      chefState: 'talking',
      position: 'right',
      showBackButton: true,
      waitForCondition: true,
      conditionCheck: 'multiComponentMode'
    },
    // Step 13: Final completion modal
    {
      type: 'modal',
      title: 'Congratulations!',
      message: "Amazing! You've learned all the ways to create recipes in 4RKED - Smart Transfer with URLs or text, and manual entry for both simple and multi-component recipes!",
      subMessage: "You're now ready to start your culinary journey. Happy cooking and happy forking!",
      chefState: 'fire',
      showStartButton: true,
      startButtonText: "Get Started"
    }
  ];

  // Utility function to create walkthrough steps easily
  const createWalkthroughStep = (type, options = {}) => {
    const defaultOptions = {
      type,
      chefState: 'talking',
      position: 'right',
      showStartButton: false,
      startButtonText: "Next"
    };
    return { ...defaultOptions, ...options };
  };

  // Example usage for creating new walkthroughs:
  // const customSteps = [
  //   createWalkthroughStep('modal', {
  //     title: 'Custom Welcome',
  //     message: 'Custom message here',
  //     showStartButton: true,
  //     startButtonText: 'Begin'
  //   }),
  //   createWalkthroughStep('highlight', {
  //     targetSelector: '[data-walkthrough="custom-button"]',
  //     message: 'Click this custom button',
  //     subMessage: 'This will do something special',
  //     position: 'left'
  //   })
  // ];

  useEffect(() => {
    if (showWalkthrough) {
      startWalkthrough();
    }
  }, [showWalkthrough]);
  
  // Track completion when final step modal is shown (but don't close it yet)
  // This ensures completion is tracked even if user doesn't click the button
  useEffect(() => {
    if (isModalOpen && currentStep === walkthroughSteps.length - 1) {
      const finalStep = walkthroughSteps[currentStep];
      if (finalStep && finalStep.type === 'modal' && !completionTrackedRef.current) {
        // Mark as tracked to prevent duplicate calls
        completionTrackedRef.current = true;
        
        // Track completion API call when final modal appears
        // This ensures the walkthrough is marked as complete in the database
        // The modal will close when user clicks "Get Started"
        const trackCompletion = async () => {
          try {
            const token = await user?.getIdToken();
            if (token) {
              // Use absolute URL in production, relative in development
              // In production, REACT_APP_BACKEND_URL should be set (e.g., https://4rked.com/api)
              // In development, use relative path which will use the proxy
              const apiBaseUrl = process.env.REACT_APP_BACKEND_URL;
              const apiUrl = apiBaseUrl 
                ? `${apiBaseUrl}/users/walkthrough-complete`
                : `/api/users/walkthrough-complete`;
              
              console.log('📡 Calling walkthrough-complete API:', apiUrl);
              
              const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                // Prevent caching
                cache: 'no-cache'
              });
              
              const responseData = await response.json().catch((e) => {
                console.error('Failed to parse response JSON:', e);
                return { error: 'Failed to parse response' };
              });
              
              if (!response.ok) {
                console.error('❌ Walkthrough completion API failed:', response.status, responseData);
              } else {
                // Verify the response indicates success
                if (responseData.success !== false && response.status === 200) {
                  console.log('✅ Walkthrough completion tracked:', responseData);
                  // Refresh user profile to get updated walkthrough_status
                  if (refreshUserProfile) {
                    setTimeout(() => {
                      refreshUserProfile();
                    }, 500);
                  }
                } else {
                  console.warn('⚠️ Walkthrough completion returned unexpected response:', responseData);
                }
              }
            } else {
              console.error('❌ No token available for walkthrough completion');
            }
          } catch (error) {
            console.error('❌ Failed to track walkthrough completion:', error);
          }
        };
        trackCompletion();
      }
    }
  }, [isModalOpen, currentStep, walkthroughSteps.length, user, refreshUserProfile]);

  // Check if element is in viewport
  const isElementInViewport = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  // Scroll element into view if needed and update scroll indicator
  useEffect(() => {
    if (highlightedElement && isChefTabVisible) {
      const checkAndScroll = () => {
        if (!isElementInViewport(highlightedElement)) {
          setShowScrollIndicator(true);
          highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          setShowScrollIndicator(false);
        }
      };
      
      // Initial check
      setTimeout(checkAndScroll, 100);
      
      // Check on scroll/resize
      window.addEventListener('scroll', checkAndScroll, true);
      window.addEventListener('resize', checkAndScroll);
      
      return () => {
        window.removeEventListener('scroll', checkAndScroll, true);
        window.removeEventListener('resize', checkAndScroll);
      };
    } else {
      setShowScrollIndicator(false);
    }
  }, [highlightedElement, isChefTabVisible]);

  // Watch for navigation to /create when on create button step
  useEffect(() => {
    if (!showWalkthrough) return;
    
    const step = walkthroughSteps[currentStep];
    if (step && step.targetSelector === '[data-walkthrough="create-button"]' && location.pathname === '/create' && !navigationProgressedRef.current) {
      // User clicked create button and navigated to /create page
      // Mark as progressed to prevent multiple triggers
      navigationProgressedRef.current = true;
      
      // Wait a bit for page to load, then proceed to next step
      setTimeout(() => {
        if (currentStep < walkthroughSteps.length - 1) {
          const nextStepIndex = currentStep + 1;
          setCurrentStep(nextStepIndex);
          
          const nextStepData = walkthroughSteps[nextStepIndex];
          
          if (nextStepData.type === 'modal') {
            setIsModalOpen(true);
            setIsChefTabVisible(false);
            setHighlightedElement(null);
            setOverlayMessage('');
            setOverlaySubMessage('');
          } else if (nextStepData.type === 'highlight') {
            setIsModalOpen(false);
            setIsChefTabVisible(true);
            setIsChefTabExpanded(true);
            // Show back/next buttons on all highlight steps
            const showBack = nextStepIndex > 0 && nextStepData.showBackButton !== false;
            const showNext = nextStepIndex < walkthroughSteps.length - 1 && (nextStepData.showNextButton !== false);
            setWalkthroughChef(
              nextStepData.message,
              nextStepData.subMessage,
              nextStepData.chefState || 'talking',
              nextStepData.position || 'right',
              showNext,
              showNext ? nextStep : null,
              showBack,
              showBack ? backStep : null
            );
            
            setTimeout(() => {
              const targetElement = document.querySelector(nextStepData.targetSelector);
              if (targetElement) {
                setHighlightedElement(targetElement);
                setOverlayMessage(nextStepData.message);
                setOverlaySubMessage(nextStepData.subMessage);
                setChefState(nextStepData.chefState);
              }
            }, 1000);
          }
        }
      }, 1000);
    }
    
    // Reset navigation progress flag when step changes
    if (step && step.targetSelector !== '[data-walkthrough="create-button"]') {
      navigationProgressedRef.current = false;
    }
  }, [location.pathname, currentStep, showWalkthrough, walkthroughSteps]);

  // Add click listeners to highlighted elements to auto-progress walkthrough
  useEffect(() => {
    if (highlightedElement && isChefTabVisible) {
      const step = walkthroughSteps[currentStep];
      
      // Handle checkbox interaction waiting
      if (step?.waitForInteraction && step?.interactionType === 'checkbox') {
        const checkbox = highlightedElement.querySelector('input[type="checkbox"]');
        if (checkbox) {
          const handleCheckboxChange = () => {
            if (checkbox.checked) {
              setTimeout(() => {
                if (currentStep < walkthroughSteps.length - 1) {
                  const nextStepIndex = currentStep + 1;
                  setCurrentStep(nextStepIndex);
                  
                  const nextStepData = walkthroughSteps[nextStepIndex];
                  
                  if (nextStepData.type === 'modal') {
                    setIsModalOpen(true);
                    setIsChefTabVisible(false);
                    setHighlightedElement(null);
                    setOverlayMessage('');
                    setOverlaySubMessage('');
                  } else if (nextStepData.type === 'highlight') {
                    setIsModalOpen(false);
                    setIsChefTabVisible(true);
                    setIsChefTabExpanded(true);
                    // Use the existing FloatingChef via context
                    // Show back/next buttons on all highlight steps
                    const showBack = nextStepIndex > 0 && nextStepData.showBackButton !== false;
                    const showNext = nextStepIndex < walkthroughSteps.length - 1 && (nextStepData.showNextButton !== false);
                    setWalkthroughChef(
                      nextStepData.message,
                      nextStepData.subMessage,
                      nextStepData.chefState || 'talking',
                      nextStepData.position || 'right',
                      showNext,
                      showNext ? nextStep : null,
                      showBack,
                      showBack ? backStep : null
                    );
                    
                    setTimeout(() => {
                      const targetElement = document.querySelector(nextStepData.targetSelector);
                      if (targetElement) {
                        setHighlightedElement(targetElement);
                        setOverlayMessage(nextStepData.message);
                        setOverlaySubMessage(nextStepData.subMessage);
                        setChefState(nextStepData.chefState);
                      }
                    }, 100);
                  }
                }
              }, 500);
            }
          };
          
          checkbox.addEventListener('change', handleCheckboxChange);
          
          return () => {
            checkbox.removeEventListener('change', handleCheckboxChange);
          };
        }
      } else {
        // Regular click handler for other elements
        // Skip auto-progression for create button - let navigation watcher handle it
        if (step?.targetSelector === '[data-walkthrough="create-button"]') {
          return; // Don't add click listener, navigation watcher will handle it
        }
        
        const handleClick = () => {
          setTimeout(() => {
            nextStep();
          }, 500);
        };

        highlightedElement.addEventListener('click', handleClick);
        
        return () => {
          highlightedElement.removeEventListener('click', handleClick);
        };
      }
    }
  }, [highlightedElement, isChefTabVisible, currentStep, walkthroughSteps]);

  // Handle recipe loading wait condition and other condition checks
  useEffect(() => {
    if (highlightedElement && isChefTabVisible) {
      const step = walkthroughSteps[currentStep];
      
      if (step?.waitForCondition) {
        if (step.conditionCheck === 'recipeLoaded') {
          // Check for recipe form being populated (look for form fields with values)
          const checkRecipeLoaded = () => {
            const recipeName = document.querySelector('input[name="name"], input[name="title"]');
            const recipeSteps = document.querySelectorAll('textarea[name*="step"], textarea[placeholder*="step"]');
            
            // Recipe is considered loaded if name field has value and at least one step has content
            if (recipeName && recipeName.value && recipeSteps.length > 0) {
              const hasSteps = Array.from(recipeSteps).some(step => step.value && step.value.trim().length > 0);
              if (hasSteps) {
                // Recipe is loaded, allow progression
                return true;
              }
            }
            return false;
          };

          const interval = setInterval(() => {
            if (checkRecipeLoaded()) {
              clearInterval(interval);
              // Don't auto-advance, just enable the button
              // User can click when ready
            }
          }, 500);

          return () => clearInterval(interval);
        } else if (step.conditionCheck === 'pasteTextMode') {
          // Check if paste text mode is active
          const checkPasteTextMode = () => {
            // Check if textarea is visible (paste text mode)
            const textarea = document.querySelector('textarea[data-walkthrough="smart-transfer"]');
            return textarea !== null && textarea.offsetParent !== null;
          };

          const interval = setInterval(() => {
            if (checkPasteTextMode()) {
              clearInterval(interval);
              // Re-highlight the element now that it's in the correct mode
              setTimeout(() => {
                const targetElement = document.querySelector(step.targetSelector);
                if (targetElement) {
                  setHighlightedElement(targetElement);
                  if (!isElementInViewport(targetElement)) {
                    setShowScrollIndicator(true);
                  } else {
                    setShowScrollIndicator(false);
                  }
                }
              }, 100);
            }
          }, 500);

          return () => clearInterval(interval);
        } else if (step.conditionCheck === 'multiComponentMode') {
          // Check if multi-component mode is active
          const checkMultiComponentMode = () => {
            // Check if add component button is visible (multi-component mode)
            const addComponentButton = document.querySelector('[data-walkthrough="add-component-button"]');
            return addComponentButton !== null && addComponentButton.offsetParent !== null;
          };

          const interval = setInterval(() => {
            if (checkMultiComponentMode()) {
              clearInterval(interval);
              // Re-highlight the element now that multi-component mode is active
              setTimeout(() => {
                const targetElement = document.querySelector(step.targetSelector);
                if (targetElement) {
                  setHighlightedElement(targetElement);
                  if (!isElementInViewport(targetElement)) {
                    setShowScrollIndicator(true);
                  } else {
                    setShowScrollIndicator(false);
                  }
                }
              }, 100);
            }
          }, 500);

          return () => clearInterval(interval);
        }
      }
    }
  }, [highlightedElement, isChefTabVisible, currentStep, walkthroughSteps]);

  const startWalkthrough = () => {
    setCurrentStep(0);
    setIsModalOpen(true);
    setChefState('talking');
  };

  const backStep = () => {
    if (currentStep > 0) {
      const prevStepIndex = currentStep - 1;
      setCurrentStep(prevStepIndex);
      
      const step = walkthroughSteps[prevStepIndex];
      
      if (step.type === 'modal') {
        setIsModalOpen(true);
        setIsChefTabVisible(false);
        setHighlightedElement(null);
        setOverlayMessage('');
        setOverlaySubMessage('');
        setChefState(step.chefState);
      } else if (step.type === 'highlight') {
        setIsModalOpen(false);
        setIsChefTabVisible(true);
        setIsChefTabExpanded(true);
        
        // Navigate back if needed
        if (step.navigateTo && location.pathname !== step.navigateTo) {
          navigate(step.navigateTo);
        }
        
        // Show back/next buttons on all highlight steps
        const showBack = prevStepIndex > 0 && step.showBackButton !== false;
        const showNext = prevStepIndex < walkthroughSteps.length - 1 && (step.showNextButton !== false);
        setWalkthroughChef(
          step.message,
          step.subMessage,
          step.chefState || 'talking',
          step.position || 'right',
          showNext,
          showNext ? nextStep : null,
          showBack,
          showBack ? backStep : null
        );
        
        // Wait a bit for elements to be available, then find and highlight
        setTimeout(() => {
          const targetElement = document.querySelector(step.targetSelector);
          
          if (targetElement) {
            setHighlightedElement(targetElement);
            setOverlayMessage(step.message);
            setOverlaySubMessage(step.subMessage);
            
            // Check if element is in viewport, show scroll indicator if not
            if (!isElementInViewport(targetElement)) {
              setShowScrollIndicator(true);
            } else {
              setShowScrollIndicator(false);
            }
          } else {
            // If element not found, try again after a short delay
            setTimeout(() => {
              const retryElement = document.querySelector(step.targetSelector);
              if (retryElement) {
                setHighlightedElement(retryElement);
                setOverlayMessage(step.message);
                setOverlaySubMessage(step.subMessage);
                
                if (!isElementInViewport(retryElement)) {
                  setShowScrollIndicator(true);
                } else {
                  setShowScrollIndicator(false);
                }
              }
            }, 500);
          }
        }, 1000);
        
        setChefState(step.chefState);
      }
    }
  };

  const nextStep = () => {
    // Check if we're on the final step - if so, complete the walkthrough
    if (currentStep === walkthroughSteps.length - 1) {
      handleCompleteWalkthrough();
      return;
    }
    
    if (currentStep < walkthroughSteps.length - 1) {
      const nextStepIndex = currentStep + 1;
      setCurrentStep(nextStepIndex);
      
      const step = walkthroughSteps[nextStepIndex];
      
      if (step.type === 'modal') {
        setIsModalOpen(true);
        setIsChefTabVisible(false);
        setHighlightedElement(null);
        setOverlayMessage('');
        setOverlaySubMessage('');
      } else if (step.type === 'highlight') {
        setIsModalOpen(false);
        setIsChefTabVisible(true);
        setIsChefTabExpanded(true);
        // Use the existing FloatingChef via context
        // Show back/next buttons on all highlight steps
        const showBack = currentStep > 0 && step.showBackButton !== false;
        const showNext = nextStepIndex < walkthroughSteps.length - 1 && (step.showNextButton !== false);
        setWalkthroughChef(
          step.message,
          step.subMessage,
          step.chefState || 'talking',
          step.position || 'right',
          showNext,
          showNext ? nextStep : null,
          showBack,
          showBack ? backStep : null
        );
        
        // Navigate to the required page if specified
        if (step.navigateTo && location.pathname !== step.navigateTo) {
          navigate(step.navigateTo);
          // If we navigated, wait longer for page to load before highlighting
          const waitTime = step.waitForPage ? 1500 : 1000;
          setTimeout(() => {
            const targetElement = document.querySelector(step.targetSelector);
            if (targetElement) {
              setHighlightedElement(targetElement);
              setOverlayMessage(step.message);
              setOverlaySubMessage(step.subMessage);
              setChefState(step.chefState);
              if (!isElementInViewport(targetElement)) {
                setShowScrollIndicator(true);
              } else {
                setShowScrollIndicator(false);
              }
            } else {
              // Retry if element not found
              setTimeout(() => {
                const retryElement = document.querySelector(step.targetSelector);
                if (retryElement) {
                  setHighlightedElement(retryElement);
                  setOverlayMessage(step.message);
                  setOverlaySubMessage(step.subMessage);
                  setChefState(step.chefState);
                  if (!isElementInViewport(retryElement)) {
                    setShowScrollIndicator(true);
                  } else {
                    setShowScrollIndicator(false);
                  }
                }
              }, 500);
            }
          }, waitTime);
          return;
        }
        
        // Wait a bit for elements to be available, then find and highlight
        const waitTime = step.waitForPage ? 1000 : 500;
        setTimeout(() => {
          const targetElement = document.querySelector(step.targetSelector);
          
          if (targetElement) {
            setHighlightedElement(targetElement);
            setOverlayMessage(step.message);
            setOverlaySubMessage(step.subMessage);
            setChefState(step.chefState);
            
            // Check if element is in viewport, show scroll indicator if not
            if (!isElementInViewport(targetElement)) {
              setShowScrollIndicator(true);
            } else {
              setShowScrollIndicator(false);
            }
          } else {
            // If element not found, try again after a short delay
            setTimeout(() => {
              const retryElement = document.querySelector(step.targetSelector);
              if (retryElement) {
                setHighlightedElement(retryElement);
                setOverlayMessage(step.message);
                setOverlaySubMessage(step.subMessage);
                setChefState(step.chefState);
                
                if (!isElementInViewport(retryElement)) {
                  setShowScrollIndicator(true);
                } else {
                  setShowScrollIndicator(false);
                }
              }
            }, 500);
          }
        }, waitTime);
      }
    } else {
      // Walkthrough complete
      handleCompleteWalkthrough();
    }
  };


  const handleCompleteWalkthrough = () => {
    setIsModalOpen(false);
    setIsChefTabVisible(false);
    setHighlightedElement(null);
    setOverlayMessage('');
    setOverlaySubMessage('');
    clearWalkthroughChef(); // Clear walkthrough chef
    setCurrentStep(0);
    // Mark walkthrough as complete
    completeWalkthrough();
  };

  const handleCloseWalkthrough = (doNotShowAgain = false) => {
    setIsModalOpen(false);
    setIsChefTabVisible(false);
    setHighlightedElement(null);
    setOverlayMessage('');
    setOverlaySubMessage('');
    clearWalkthroughChef(); // Clear walkthrough chef
    setCurrentStep(0);
    
    // If "do not show again" is checked, mark as completed instead of just closed
    if (doNotShowAgain) {
      completeWalkthrough();
    } else {
      closeWalkthrough();
    }
  };

  const handleStopWalkthrough = (doNotShowAgain = false) => {
    setIsModalOpen(false);
    setIsChefTabVisible(false);
    setHighlightedElement(null);
    setOverlayMessage('');
    setOverlaySubMessage('');
    clearWalkthroughChef(); // Clear walkthrough chef
    setCurrentStep(0);
    
    // If "do not show again" is checked, mark as completed instead of just stopped
    if (doNotShowAgain) {
      completeWalkthrough();
    } else {
      stopWalkthrough();
    }
  };

  const toggleChefTab = () => {
    setIsChefTabExpanded(!isChefTabExpanded);
  };

  const currentStepData = walkthroughSteps[currentStep];

  // Only render for authenticated users
  if (!user) {
    return null;
  }

  // Check if we're on the final step
  const isFinalStep = currentStep === walkthroughSteps.length - 1;
  
  return (
    <div className={className}>
      {/* Welcome Modal */}
      <WalkthroughModal
        isOpen={isModalOpen}
        onClose={handleCloseWalkthrough}
        onStart={isFinalStep ? handleCompleteWalkthrough : nextStep}
        onStop={handleStopWalkthrough}
        title={currentStepData?.title}
        message={currentStepData?.message}
        subMessage={currentStepData?.subMessage}
        showChef={true}
        chefState={currentStepData?.chefState}
        showStartButton={currentStepData?.showStartButton}
        startButtonText={currentStepData?.startButtonText}
        showStopButton={true}
        showDoNotShowAgain={currentStepData?.showDoNotShowAgain || false}
      />

      {/* Floating Chef Tab is now controlled via WalkthroughChefContext and rendered in FloatingChef */}

      {/* Walkthrough Overlay - Only for highlighting, no messages */}
      <WalkthroughOverlay
        isActive={isChefTabVisible && !isModalOpen}
        highlightedElement={highlightedElement}
        message=""
        subMessage=""
        position={currentStepData?.position || 'right'}
      />

      {/* Scroll Indicator - Shows arrow when target element is out of view */}
      <ScrollIndicator
        targetElement={highlightedElement}
        isActive={isChefTabVisible && !isModalOpen && showScrollIndicator}
      />
    </div>
  );
};

export default WalkthroughManager;
